import {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { t } from 'services/translation';
import { PhoneInput } from './PhoneInput';
import {
  PersonalContainer,
  FormFieldsContainer,
  SectionHeader,
  StepIndicator,
  FieldRequirementsText,
  FormSection,
  FormLabel,
  HintText,
  InputGroup,
  InputWrapper,
  StyledInput,
  ErrorText,
  OptionalText,
  HiddenInput,
} from './styles';
import {
  ValidationState,
  validateField,
  getFieldErrors,
  hasFieldError,
  scrollToFirstError,
  shouldShowNameFields,
  shouldShowEmailField,
} from './Helpers';

interface PersonalInformationProps {
  fields: {
    get: (field: string) => string | null;
  };

  errors: {
    get: (field: string) => string[] | undefined;
  };

  onChange: (field: string, value: string) => void;
  googleTagManagerCheckoutProgress?: (step: number) => void;

  borderless?: boolean;
  fieldsToHide?: string[];
  is_booking_invoice?: boolean;
  isLiveBooking?: boolean;
  isAffiliate?: boolean;
  phoneMandatory?: boolean;
  showZipCodeOnLive?: boolean;
  countryCode?: string;

  fieldRequirementsSubtext?: string;
  emailSubtext?: string;
  phoneSubtext?: string;
}

export interface PersonalInformationRef {
  validateAllFields: () => boolean;
  scrollToFirstError: () => void;
}

export const PersonalInformation = forwardRef<
  PersonalInformationRef,
  PersonalInformationProps
>(
  (
    {
      fields,
      errors,
      onChange,
      googleTagManagerCheckoutProgress,
      borderless = false,
      fieldsToHide = [],
      is_booking_invoice = false,
      isLiveBooking = false,
      isAffiliate = false,
      phoneMandatory = false,
      showZipCodeOnLive = false,
      countryCode = 'US',
      fieldRequirementsSubtext,
      emailSubtext,
      phoneSubtext,
    },
    ref
  ) => {
    const personalInfoRef = useRef<HTMLDivElement>(null);
    const [localValidation, setLocalValidation] = useState<ValidationState>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // Handle focus tracking for analytics
    useEffect(() => {
      const element = personalInfoRef.current;
      if (!element || !googleTagManagerCheckoutProgress) return;

      const handleFocus = () => {
        googleTagManagerCheckoutProgress(2);
      };

      element.addEventListener('focus', handleFocus, {
        capture: true,
        once: true,
      });

      return () => {
        element.removeEventListener('focus', handleFocus, { capture: true });
      };
    }, [googleTagManagerCheckoutProgress]);

    // Validation helper
    const validateFieldCallback = useCallback(
      (fieldName: string, value: string): string[] => {
        return validateField(
          fieldName,
          value,
          phoneMandatory,
          showZipCodeOnLive,
          t
        );
      },
      [phoneMandatory, showZipCodeOnLive]
    );

    // Handle field changes
    const handleFieldChange = useCallback(
      (fieldName: string, value: string) => {
        onChange(fieldName, value);

        // Clear local validation errors when user starts typing
        if (localValidation[fieldName]) {
          setLocalValidation((prev) => ({
            ...prev,
            [fieldName]: [],
          }));
        }
      },
      [onChange, localValidation]
    );

    // Handle name blur with cardholder auto-fill
    const handleNameBlur = useCallback(
      (nameField: 'first_name' | 'last_name', value: string) => {
        // Auto-fill cardholder name if empty
        const cardholderField = `cardholder_${nameField}`;
        const cardholderValue = fields.get(cardholderField);

        if (value && !cardholderValue) {
          onChange(cardholderField, value);
        }

        // Only validate if we're showing validation errors (after submit)
        if (showValidationErrors) {
          const fieldErrors = validateFieldCallback(nameField, value);
          setLocalValidation((prev) => ({
            ...prev,
            [nameField]: fieldErrors,
          }));
        }
      },
      [fields, onChange, validateFieldCallback, showValidationErrors]
    );

    // Handle other field blur
    const handleFieldBlur = useCallback(
      (fieldName: string, value: string) => {
        // Only validate if we're showing validation errors (after submit)
        if (showValidationErrors) {
          const fieldErrors = validateFieldCallback(fieldName, value);
          setLocalValidation((prev) => ({
            ...prev,
            [fieldName]: fieldErrors,
          }));
        }
      },
      [validateFieldCallback, showValidationErrors]
    );

    // Handle blur events
    const handleBlur = useCallback(
      (fieldName: string, value: string) => {
        handleFieldBlur(fieldName, value);
      },
      [handleFieldBlur]
    );

    // Handle email change with trimming
    const handleEmailChange = useCallback(
      (value: string) => {
        const trimmedValue = value.trim();
        handleFieldChange('email', trimmedValue);
      },
      [handleFieldChange]
    );

    // Get errors for a field (server + local validation)
    const getFieldErrorsCallback = useCallback(
      (fieldName: string): string[] => {
        const serverErrors = errors.get(fieldName) || [];
        const localErrors = localValidation[fieldName] || [];
        return getFieldErrors(
          fieldName,
          serverErrors,
          localErrors,
          showValidationErrors
        );
      },
      [errors, localValidation, showValidationErrors]
    );

    const hasFieldErrorCallback = useCallback(
      (fieldName: string): boolean => {
        const fieldErrors = getFieldErrorsCallback(fieldName);
        return hasFieldError(fieldErrors);
      },
      [getFieldErrorsCallback]
    );

    // Validate all fields and show errors
    const validateAllFields = useCallback(() => {
      const allErrors: ValidationState = {};
      let hasErrors = false;

      // Validate all form fields
      const fieldsToValidate = ['first_name', 'last_name', 'email', 'phone'];

      fieldsToValidate.forEach((fieldName) => {
        const value = fields.get(fieldName) || '';
        const fieldErrors = validateFieldCallback(fieldName, value);
        if (fieldErrors.length > 0) {
          allErrors[fieldName] = fieldErrors;
          hasErrors = true;
        }
      });

      setLocalValidation(allErrors);
      setShowValidationErrors(true);

      // Scroll to first error using the allErrors we just computed
      if (hasErrors) {
        scrollToFirstError(
          (fieldName) => allErrors[fieldName] || [],
          personalInfoRef
        );
      }

      return !hasErrors;
    }, [fields, validateFieldCallback]);

    // Scroll to first error field
    const scrollToFirstErrorCallback = useCallback(() => {
      scrollToFirstError(getFieldErrorsCallback, personalInfoRef);
    }, [getFieldErrorsCallback]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      validateAllFields,
      scrollToFirstError: scrollToFirstErrorCallback,
    }));

    // Field visibility checks
    const shouldShowNameFieldsValue = shouldShowNameFields(fieldsToHide);
    const shouldShowEmailFieldValue = shouldShowEmailField(fieldsToHide);

    return (
      <PersonalContainer
        ref={personalInfoRef}
        borderless={borderless}
        isLive={isLiveBooking}
        data-testid="personal-section"
      >
        {fieldRequirementsSubtext && (
          <FieldRequirementsText>
            {fieldRequirementsSubtext}
          </FieldRequirementsText>
        )}

        <SectionHeader>
          <StepIndicator>1</StepIndicator>
          {isAffiliate ? t('GUEST_INFORMATION') : t('YOUR_INFORMATION')}
        </SectionHeader>

        <FormFieldsContainer borderless={borderless}>
          {/* Name Fields */}
          {shouldShowNameFieldsValue && (
            <FormSection>
              <FormLabel htmlFor="personal-name-fields">
                {isAffiliate ? t('GUEST_NAME') : t('NAME')}
              </FormLabel>
              <InputGroup>
                <InputWrapper>
                  <StyledInput
                    type="text"
                    name="first_name"
                    data-field="first_name"
                    data-testid="firstname-input"
                    placeholder={t('FIRST')}
                    value={fields.get('first_name') || ''}
                    hasError={hasFieldErrorCallback('first_name')}
                    onChange={(e) =>
                      handleFieldChange('first_name', e.target.value)
                    }
                    onBlur={(e) => handleNameBlur('first_name', e.target.value)}
                    autoComplete="given-name"
                  />
                  {getFieldErrorsCallback('first_name').map((error, index) => (
                    <ErrorText key={index}>{error}</ErrorText>
                  ))}
                </InputWrapper>
                <InputWrapper>
                  <StyledInput
                    type="text"
                    name="last_name"
                    data-field="last_name"
                    data-testid="lastname-input"
                    placeholder={t('LAST')}
                    value={fields.get('last_name') || ''}
                    hasError={hasFieldErrorCallback('last_name')}
                    onChange={(e) =>
                      handleFieldChange('last_name', e.target.value)
                    }
                    onBlur={(e) => handleNameBlur('last_name', e.target.value)}
                    autoComplete="family-name"
                  />
                  {getFieldErrorsCallback('last_name').map((error, index) => (
                    <ErrorText key={index}>{error}</ErrorText>
                  ))}
                </InputWrapper>
              </InputGroup>
            </FormSection>
          )}

          {/* Email Field */}
          {shouldShowEmailFieldValue && (
            <FormSection>
              <FormLabel htmlFor="email">
                {isAffiliate ? t('GUEST_EMAIL_ADDRESS') : t('EMAIL_ADDRESS')}
              </FormLabel>
              {!isLiveBooking && !isAffiliate && (
                <HintText>
                  {emailSubtext || t('RECEIPTS_AND_NOTIFICATIONS')}
                </HintText>
              )}
              {!isLiveBooking && isAffiliate && (
                <HintText>{t('IF_EMAIL_PROVIDED')}</HintText>
              )}
              <StyledInput
                type="email"
                name="email"
                id="email"
                data-field="email"
                data-testid="email-input"
                placeholder={`${t('EG')} ${t('EMAIL').toLowerCase()}@${t('EXAMPLE')}.com`}
                value={fields.get('email') || ''}
                hasError={hasFieldErrorCallback('email')}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={(e) => handleFieldBlur('email', e.target.value)}
                autoComplete="email"
              />
              {getFieldErrorsCallback('email').map((error, index) => (
                <ErrorText key={index}>{error}</ErrorText>
              ))}
            </FormSection>
          )}

          {/* Phone Field */}
          <FormSection>
            <FormLabel htmlFor="phone">
              {isAffiliate ? `${t('GUEST_PHONE')} ` : `${t('PHONE')} `}
              {!phoneMandatory && (
                <OptionalText>({t('OPTIONAL')})</OptionalText>
              )}
            </FormLabel>
            <HintText>
              {phoneSubtext ||
                t(
                  'BY_PROVIDING_TOUR_PHONE_NUMBER_YOU_AGREE_TO_RECEIVE_SMS_MESSAGES_RELATED_TO_THIS_BOOKING'
                )}
            </HintText>
            <PhoneInput
              phoneNumber={fields.get('phone') || ''}
              setPhoneNumber={(value) => handleFieldChange('phone', value)}
              countryCode={countryCode}
              hasError={hasFieldErrorCallback('phone')}
              dataTestId="country-code-selector"
              ariaLabelledBy="phone-field"
              onBlur={() => handleBlur('phone', fields.get('phone') || '')}
            />
            {getFieldErrorsCallback('phone').map((error, index) => (
              <ErrorText key={index}>{error}</ErrorText>
            ))}
          </FormSection>

          {/* Zip Code Field (Live Bookings) */}
          {isLiveBooking && showZipCodeOnLive && (
            <FormSection>
              <FormLabel htmlFor="zip_code">{t('ZIP_POSTAL_CODE')}</FormLabel>
              <StyledInput
                type="text"
                name="zip_code"
                id="zip_code"
                data-field="zip_code"
                data-testid="zip-input"
                value={fields.get('zip_code') || ''}
                hasError={hasFieldErrorCallback('zip_code')}
                onChange={(e) => handleFieldChange('zip_code', e.target.value)}
                onBlur={(e) => handleFieldBlur('zip_code', e.target.value)}
                maxLength={15}
                autoComplete="postal-code"
              />
              {getFieldErrorsCallback('zip_code').map((error, index) => (
                <ErrorText key={index}>{error}</ErrorText>
              ))}
            </FormSection>
          )}
        </FormFieldsContainer>

        {/* Hidden Fields */}
        <HiddenInput
          type="hidden"
          name="guest_invoiced"
          value={is_booking_invoice ? 'true' : 'false'}
          onChange={() => {}} // Controlled by props
        />
        <HiddenInput
          type="hidden"
          name="is_live_booking"
          value={isLiveBooking ? 'true' : 'false'}
          onChange={() => {}} // Controlled by props
        />
      </PersonalContainer>
    );
  }
);
